import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Popconfirm, message, Space, Typography, Switch } from 'antd';
import axios from 'axios';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import useUserProfile from '../../useUserProfile';
const { Title } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const MainBudgetAdminPanel: React.FC = () => {
    const [budgets, setBudgets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingBudget, setEditingBudget] = useState<any>(null);
    const [form] = Form.useForm();
    const { instance, accounts } = useMsal();
    const { requestProfileData } = useUserProfile();
    const [UserDetails, setUserDetails] = useState<{ id: string, displayName: string, email: string }[]>([]);
    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                await requestProfileData();
                const userMail = localStorage.getItem("UserMail");
                const userName = localStorage.getItem("UserName");
                const userId = localStorage.getItem("UserId");
                setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);

            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };

        fetchProfileData();
    }, [requestProfileData]);
    const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });
        return res.idToken;
    };

    // Fetch all main budgets
    const fetchBudgets = async () => {
        setLoading(true);
        try {
            const token = await fetchToken();

            const res = await axios.get(`${API_URL}/api/budget/get-main-budget`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setBudgets(res.data);
        } catch (err) {
            message.error('Failed to fetch budgets');
        }
        setLoading(false);
    };



    useEffect(() => {
        fetchBudgets();
    }, []);

    // Handle create or update
    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingBudget) {
                const token = await fetchToken();
                const userId = UserDetails[0].id;
                if (!userId) {
                    message.error('User ID not found');
                    return;
                }
                // Update
                await axios.put(`${API_URL}/api/budget/main`, {
                    BudgetId: editingBudget.budgetId,
                    TotalBudget: values.TotalBudget,
                    UsedBudget: values.UsedBudget,
                    Balance: values.Balance,
                    Comments: values.Comments,
                    IsActive: values.IsActive,
                    UpdatedBy: userId,
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                message.success('Budget updated');
            } else {
                const token = await fetchToken();

                // Create
                await axios.post(`${API_URL}/api/budget/main`, {
                    TotalBudget: values.TotalBudget,
                    AdminId: UserDetails[0].id,
                    Comments: values.Comments,
                    CarriesForward: values.CarriesForward,
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                message.success('Budget created');
            }
            setModalVisible(false);
            setEditingBudget(null);
            form.resetFields();
            fetchBudgets();
        } catch (err: any) {
            message.error(err?.response?.data || 'Operation failed');
        }
    };

    // Handle delete
    const handleDelete = async (id: string) => {
        try {
            const token = await fetchToken();
            const userId = UserDetails[0].id;
            if (!userId) {
                message.error('User ID not found');
                return;
            }
            await axios.delete(`${API_URL}/api/budget/main/${id}/${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            message.success('Budget deleted');
            fetchBudgets();
        } catch {
            message.error('Failed to delete');
        }
    };

    // Open modal for edit/create
    const openModal = (budget?: any) => {
        setEditingBudget(budget || null);
        setModalVisible(true);
        if (budget) {
            form.setFieldsValue({
                TotalBudget: budget.totalBudget,
                UsedBudget: budget.usedBudget,
                Balance: budget.balance,
                Comments: budget.comments,
                IsActive: budget.isActive,
                AdminId: budget.adminId,
                CarriesForward: budget.carriedOver,
            });
        } else {
            form.resetFields();
        }
    };

    const columns = [
        { 
            title: 'Total Budget', 
            dataIndex: 'totalBudget', 
            key: 'totalBudget',
            ellipsis: { showTitle: true },
            width: 150,
        },
        { 
            title: 'Used Budget', 
            dataIndex: 'usedBudget', 
            key: 'usedBudget',
            ellipsis: { showTitle: true },
            width: 150,
        },
        { 
            title: 'Balance', 
            dataIndex: 'balance', 
            key: 'balance',
            ellipsis: { showTitle: true },
            width: 150,
        },
        { 
            title: 'Comments', 
            dataIndex: 'comments', 
            key: 'comments',
            ellipsis: { showTitle: true },
            width: 200,
        },
        { 
            title: 'Active', 
            dataIndex: 'isActive', 
            key: 'isActive', 
            render: (v: boolean) => v ? 'Yes' : 'No',
            width: 100,
        },
        { 
            title: 'Carried Over', 
            dataIndex: 'carriedOver', 
            key: 'carriedOver', 
            render: (v: boolean) => v ? 'Yes' : 'No',
            width: 120,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: any, record: any) => (
                <Space>
                    <Button type="link" onClick={() => openModal(record)}>Edit</Button>
                    {/* <Popconfirm title="Delete this budget?" onConfirm={() => handleDelete(record.budgetId)}>
                        <Button type="link" danger>Delete</Button>
                    </Popconfirm> */}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Title level={3}>Main Budget Management</Title>
            <Button type="primary" onClick={() => openModal()} style={{ marginBottom: 16 }}>
                Create New Budget
            </Button>
            <Table
                columns={columns}
                dataSource={budgets}
                rowKey="budgetId"
                loading={loading}
                bordered
                pagination={{ pageSize: 5 }}
                scroll={{ x: 'max-content' }}
            />
            <Modal
                title={editingBudget ? 'Edit Budget' : 'Create Budget'}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => { setModalVisible(false); setEditingBudget(null); }}
                okText={editingBudget ? 'Update' : 'Create'}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Total Budget"
                        name="TotalBudget"
                        rules={[{ required: true, message: 'Total Budget is required' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    {editingBudget && (
                        <>
                            <Form.Item label="Used Budget" name="UsedBudget">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item label="Balance" name="Balance">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item label="Active" name="IsActive" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item label="Comments" name="Comments">
                        <Input.TextArea />
                    </Form.Item>
                    {!editingBudget && (
                        <>
                            <Form.Item label="Carries Forward" name="CarriesForward" valuePropName="checked">
                                <Switch defaultChecked />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default MainBudgetAdminPanel;